/** Staff console frame: Entra-only sign-in (C04-003), role-aware nav, roles from server-side membership only. */
import { useEffect, useMemo } from 'react';
import { Outlet, useSearchParams } from 'react-router';
import { useRegisterSurfaceNav, type SurfaceNavItem } from '../../../shell/surface-nav';
import { customerOriginFor, partnerOriginFor } from '@qm/tenancy/surface';
import { EntryLayout, Heading, Text } from '@qm/ui';
import { useOptionalApp } from '../../../lib/app-context';
import { authDestination, rememberNext, safeNext } from '../../../lib/deeplink';
import { SurfaceProvider, useSurface } from '../kit/context';
import { errorInfo } from '../kit/http';
import { useResource } from '../kit/resource';
import { ErrorNote } from '../kit/ui';
import { adminApi } from '../api';
import { canSeeRevenue, canSupport, isFinance, isStaff, isSuperAdmin } from '../lib/logic';
import { canProposePrice, canViewCms, canViewUiText } from '../lib/cms-logic';
import en from '../i18n/en.json';
import ar from '../i18n/ar.json';
import '../kit/kit.css';
import './cms.css';
import './admin.css';

export const CATALOG = { en, ar } as const;

export function useAdminApi() {
  const { http } = useSurface();
  return useMemo(() => adminApi(http), [http]);
}

type Cat = 'customers' | 'operations' | 'finance' | 'platform';
type NavItem = { to: string; key: string; show: (roles: readonly string[]) => boolean; group?: 'website'; cat?: Cat };
const all = () => true;
export const NAV: readonly NavItem[] = [
  { to: '/tenants', key: 'portal.admin.nav.tenants', show: all, cat: 'customers' },
  { to: '/customers', key: 'portal.admin.nav.customers', show: all, cat: 'customers' },
  { to: '/crm', key: 'portal.admin.nav.crm', show: all, cat: 'customers' },
  { to: '/leads', key: 'portal.admin.nav.leads', show: all, cat: 'customers' },
  { to: '/opportunities', key: 'opportunities.admin.nav', show: (r) => ['staff_admin', 'staff_ops', 'staff_support', 'staff_finance', 'staff_readonly'].some((x) => r.includes(x)), cat: 'customers' },
  { to: '/partners', key: 'portal.admin.nav.partners', show: canSupport, cat: 'customers' },
  { to: '/diagnostics', key: 'portal.admin.nav.diagnostics', show: canSupport, cat: 'operations' },
  { to: '/rescue', key: 'portal.admin.nav.rescue', show: canSupport, cat: 'operations' },
  { to: '/support-queue', key: 'portal.admin.nav.supportQueue', show: canSupport, cat: 'operations' },
  // Advanced audit expert review desk (T10; the server gates on flag audit.expert + staff roles)
  { to: '/audit-expert', key: 'portal.admin.nav.auditExpert', show: canSupport, cat: 'operations' },
  { to: '/growth-audit', key: 'growth-audit.control.title', show: isSuperAdmin, cat: 'operations' },
  { to: '/billing', key: 'portal.admin.nav.billing', show: canSeeRevenue, cat: 'finance' },
  { to: '/costs', key: 'portal.admin.nav.costs', show: isFinance, cat: 'finance' },
  { to: '/ai', key: 'portal.admin.nav.ai', show: (r) => isSuperAdmin(r) || isFinance(r), cat: 'finance' },
  { to: '/flags', key: 'portal.admin.nav.flags', show: isSuperAdmin, cat: 'platform' },
  { to: '/access', key: 'portal.admin.nav.access', show: (r) => r.some((x) => (x.startsWith('staff_') && x !== 'staff_content') || x === 'platform_owner'), cat: 'platform' },
  { to: '/staff', key: 'portal.admin.nav.staff', show: isSuperAdmin, cat: 'platform' },
    { to: '/content-admin', key: 'portal.admin.nav.contentAdmin', show: isSuperAdmin, cat: 'platform' },
  // Website CMS group: editors (staff_content) and Super Admin edit; ops/support/read-only view; prices for proposers.
  { to: '/cms/pages', key: 'portal.admin.nav.cmsPages', show: canViewCms, group: 'website' },
  { to: '/cms/media', key: 'portal.admin.nav.cmsMedia', show: canViewCms, group: 'website' },
  { to: '/cms/collections', key: 'portal.admin.nav.cmsCollections', show: canViewCms, group: 'website' },
  { to: '/cms/text', key: 'portal.admin.nav.cmsText', show: canViewUiText, group: 'website' },
  { to: '/cms/prices', key: 'portal.admin.nav.cmsPrices', show: canProposePrice, group: 'website' },
];
export const navFor = (roles: readonly string[]) => NAV.filter((n) => n.show(roles));

const ADMIN_ICON: Record<string, SurfaceNavItem['icon']> = {
  '/tenants': 'team', '/customers': 'leads', '/crm': 'billing', '/leads': 'leads', '/opportunities': 'billing', '/partners': 'team', '/diagnostics': 'settings', '/rescue': 'bell', '/support-queue': 'inbox', '/audit-expert': 'shield',
  '/billing': 'billing', '/costs': 'billing', '/ai': 'settings', '/flags': 'settings', '/staff': 'shield', '/content-admin': 'content',
  '/cms/pages': 'content', '/cms/media': 'content', '/cms/collections': 'content', '/cms/text': 'content', '/cms/prices': 'billing',
};

const CAT_ORDER: readonly Cat[] = ['customers', 'operations', 'finance', 'platform'];

/** The staff menu shown in the shell's left drawer, grouped into plain-language categories (role-filtered; empty categories vanish). */
export function adminNavItems(roles: readonly string[], t: (key: string) => string): SurfaceNavItem[] {
  const visible = navFor(roles);
  const item = (n: NavItem, group?: string): SurfaceNavItem => ({ to: n.to, label: t(n.key), icon: ADMIN_ICON[n.to] ?? 'settings', ...(group ? { group } : {}) });
  return [
    { to: '/', label: t('portal.admin.nav.home'), icon: 'home', group: t('portal.admin.nav.group.overview'), tab: true },
    ...CAT_ORDER.flatMap((c) => visible.filter((n) => !n.group && n.cat === c).map((n) => ({ ...item(n, t(`portal.admin.nav.group.${c}`)), ...(n.to === '/tenants' || n.to === '/customers' || n.to === '/leads' ? { tab: true } : {}) }))),
    ...visible.filter((n) => n.group === 'website').map((n) => item(n, t('portal.admin.nav.website'))),
  ];
}

function AdminMenu({ roles }: { roles: readonly string[] }) {
  const { t } = useSurface();
  useRegisterSurfaceNav(adminNavItems(roles, t));
  return null;
}

export function AdminRoot() {
  return (
    <SurfaceProvider catalog={CATALOG}>
      <Outlet />
    </SurfaceProvider>
  );
}

/** Loads the staff session. No session (or a non-staff session) shows the Entra sign-in; nothing else renders. */
export function StaffGate() {
  const api = useAdminApi();
  const { t } = useSurface();
  const me = useResource('admin.me', api.me);
  if (me.status === 'loading') return <Text tone="muted">{t('portal.kit.loading')}</Text>;
  if (me.status === 'error') {
    const e = errorInfo(me.error);
    return e.status === 401 || e.status === 403 ? <StaffSignIn /> : <ErrorNote error={me.error} />;
  }
  // 204 from the anonymous session probe arrives as an empty body (signed out), not an error.
  if (!me.data || !isStaff(me.data.roles)) return <StaffSignIn notStaff={!!me.data} />;
  return (
    <SurfaceProvider catalog={CATALOG} roles={me.data.roles}>
      <AdminMenu roles={me.data.roles} />
      <Outlet />
    </SurfaceProvider>
  );
}

const SIGNIN_ERRORS = new Set(['mfa_required', 'wrong_tenant', 'no_role', 'account_disabled', 'invalid_state', 'wrong_host', 'idp_error', 'token_exchange_failed']);

/** Staff sign-in is Microsoft Entra ID only (Microsoft handles MFA). No password or magic-link form exists here. */
export function StaffSignIn({ notStaff }: { notStaff?: boolean }) {
  const { t, dir, locale } = useSurface();
  const app = useOptionalApp();
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));
  // The API callback redirects failed Microsoft sign-ins to /sign-in?error=<reason>; show it instead of a silent reload.
  const errorReason = params.get('error');
  const staffSession = !!app?.me && isStaff(app.me.roles);
  useEffect(() => { if (params.get('next')) rememberNext(params.get('next')); }, [params]);
  useEffect(() => {
    if (staffSession && params.get('next')) window.location.assign(authDestination(next));
  }, [staffSession, next, params]);
  // Editorial 2026: same composition as the customer sign-in (hero + form on white), restrained staff art.
  return (
    <EntryLayout art="staff" testId="staff-signin" dir={dir} lang={locale} eyebrow={t('portal.admin.signin.eyebrow')}
      headline={t('portal.admin.signin.heroTitle')} pivot={t('portal.admin.signin.heroPivot')} note={t('portal.admin.signin.note')}>
      <Heading level={1} id="page-title">{t('portal.admin.signin.title')}</Heading>
      <Text tone="muted">{t('portal.admin.signin.subtitle')}</Text>
      {notStaff || (app?.me && !staffSession) ? <Text role="alert">{t('portal.admin.signin.notStaff')}</Text> : null}
      {errorReason ? <Text role="alert" data-testid="signin-error">{t(SIGNIN_ERRORS.has(errorReason) ? `portal.admin.signin.error.${errorReason}` : 'portal.admin.signin.error.unknown')}</Text> : null}
      <a className="qm-button" data-variant="primary" href="/bff/v1/identity/entra/start" data-testid="entra-signin">{t('portal.admin.signin.entra')}</a>
      <Text tone="muted">{t('portal.admin.signin.mfa')}</Text>
      {/* Owner request 27 Sep 2026: not everyone who lands here is staff - link to the other two sign-ins. */}
      <div className="qm-entry__links">
        <Text tone="muted">
          <a href={typeof window === 'undefined' ? '/signin' : `${customerOriginFor(window.location.host, window.location.protocol)}/signin`} data-testid="customer-signin-link">{t('portal.admin.signin.customerLink')}</a>
          {' · '}
          <a href={typeof window === 'undefined' ? '/signin' : `${partnerOriginFor(window.location.host, window.location.protocol)}/signin`} data-testid="partner-signin-link">{t('portal.admin.signin.partnerLink')}</a>
        </Text>
      </div>
    </EntryLayout>
  );
}

